import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { AudiencePlatformItem, CountryItem, AudienceItem } from '@digiseg-labs/api-client';
import { useAudience } from '../hooks/useAudience';
import { ArrowRightIcon, CopyIcon, DesktopIcon, HomeIcon, PersonIcon, GlobeIcon } from '@radix-ui/react-icons';
import { Badge, Box, Button, Callout, Card, Dialog, Flex, Heading, Text, Tooltip, } from '@radix-ui/themes';
import AudienceRequestForm from './AudienceRequestForm';
import { ViewHorizontalIcon } from '@radix-ui/react-icons';
import { shouldHidePlatformId } from '../utils/platformIdVisibility';
import GenericSelect from './GenericSelect';

type Props = {
  audienceItem?: AudienceItem;
  audienceCode: string;
  categoryName?: string;
  countries: CountryItem[];
  platforms: AudiencePlatformItem[];
  selectedCountry: CountryItem | undefined;
  selectedPlatform: AudiencePlatformItem | undefined;
  onCountrySelected: (c: CountryItem | undefined) => void;
  onPlatformSelected: (p: AudiencePlatformItem | undefined) => void;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  shareControls?: ReactNode;
};

const AudienceRecommendationModal = ({
  audienceItem: initialAudienceItem,
  audienceCode,
  categoryName,
  countries,
  platforms,
  selectedCountry,
  selectedPlatform,
  onCountrySelected,
  onPlatformSelected,
  trigger,
  open,
  onOpenChange,
  shareControls,
}: Props) => {
  const [step, setStep] = useState<number>(1);
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogOpen = open ?? internalOpen;

  const { audience: fetchedAudience } = useAudience({
    audienceCode,
    country: selectedCountry,
    platform: selectedPlatform,
    enabled: dialogOpen && !!audienceCode,
  });
  const audienceItem = fetchedAudience ?? initialAudienceItem;
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetCopyFeedback = useCallback(() => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = null;
    }
    setCopyStatus('idle');
  }, []);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalOpen(nextOpen);
    }
    if (!nextOpen) {
      setStep(1);
      resetCopyFeedback();
    }
    onOpenChange?.(nextOpen);
  };
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, [resetCopyFeedback]);

  useEffect(() => {
    if (!dialogOpen) {
      setStep(1);
    }
    resetCopyFeedback();
  }, [dialogOpen, resetCopyFeedback]);

  useEffect(() => {
    resetCopyFeedback();
  }, [selectedPlatform?.code, selectedCountry?.code, audienceCode, resetCopyFeedback]);

  const categoryDisplayName = audienceItem?.category?.display_name || categoryName;

  const finalDescription =
    audienceItem?.description?.trim() ||
    `This is the audience ${audienceItem?.display_name || '(unknown)'} from the category ${categoryDisplayName || '(none)'}.`; 
  const isManualPlatform = !!selectedPlatform?.code?.startsWith('request_');
  const normalizedPlatformText = `${selectedPlatform?.code ?? ''} ${selectedPlatform?.display_name ?? ''}`.toLowerCase();
  const hidePlatformId = shouldHidePlatformId(selectedPlatform);
  const copyIdPlatforms = ['xandr', 'adform', 'ttd', 'the trade desk', 'the_trade_desk', 'equativ', 'index-exchange', 'mgid', 'pubmatic'];
  const isDV360InUS = normalizedPlatformText.includes('dv360') && selectedCountry?.code === 'US';
  const isCopyIdPlatform = !!selectedPlatform && !isManualPlatform && !hidePlatformId && (
    copyIdPlatforms.some((platform) => normalizedPlatformText.includes(platform)) || isDV360InUS
  );
  const requiresCountrySelection = !!selectedPlatform && !selectedPlatform.has_global_taxonomy && !selectedCountry;
  const canCopyId = isCopyIdPlatform && !!audienceItem?.platform_code && !requiresCountrySelection;

  function handleFormSubmit(data: any): Promise<void> {
    console.log('Form submitted with data:', data);
    return Promise.resolve();
  }

  const handleCopyId = async () => {
    if (!audienceItem?.platform_code) return;
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      setCopyStatus('error');
      return;
    }

    try {
      await navigator.clipboard.writeText(audienceItem.platform_code);
      setCopyStatus('copied');
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopyStatus('idle');
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy ID', error);
      setCopyStatus('error');
    }
  };

  const shouldRenderTrigger = !!trigger || open === undefined;
  const triggerContent = trigger ?? (
    <Card className="linkcursor shadoweffect recommendation-trigger">
      <Flex align="center" justify="between">
        <Box className="normalpadding">
          <Badge size="2">{categoryDisplayName || '(none)'}</Badge>
          <Heading as="h2" size="5">
            {audienceItem?.display_name}
          </Heading>
        </Box>
        <Box>
          <Button variant="outline">See More <ArrowRightIcon /></Button>
        </Box>
      </Flex>
    </Card>
  );

  return (
    <Dialog.Root open={dialogOpen} onOpenChange={handleDialogOpenChange}>
      {shouldRenderTrigger && (
        <Dialog.Trigger>
          {triggerContent}
        </Dialog.Trigger>
      )}

      <Dialog.Content maxWidth="800px">
        <Box>
          <Flex gap="2" mb="4" align="start" justify="between">
            <Box width="50%">
              <Badge size="3" color="green">
                {categoryDisplayName || '(none)'}
              </Badge>
              <Heading>{audienceItem?.display_name}</Heading>
            </Box>

            <Flex direction="column" gap="2" width="50%" align="end">
              {shareControls}
                <GenericSelect
                  items={countries || []}
                  selectedItem={selectedCountry}
                  onItemChange={onCountrySelected}
                  placeholder="Select country…"
                  modalTrigger={{enabled: true, label: "Country Selector", icon: <ViewHorizontalIcon/>, onTrigger: () => {}}}
                  sectionLabel='Countries'
                  emptyOption={{ enabled: true, label: 'No Country', icon: <GlobeIcon/>}}
                  />
            </Flex>
          </Flex>

          {step === 1 && (
            <>
              <Flex gap="2" mb="4">
                <Box width="100%">
                  <Heading color="gray" as="h3" size="2">Description</Heading>
                  <hr />
                  <Text>{finalDescription}</Text>
                </Box>
              </Flex>

              <Flex gap="2" mb="4">
                <Box width="50%">
                  <Heading color="gray" as="h3" size="2">Activate on Platform</Heading>
                  <hr />
                  <Box width="100%">
                    <GenericSelect 
                      items={platforms} 
                      selectedItem={selectedPlatform} 
                      onItemChange={onPlatformSelected} 
                      withSearch={true} 
                      renderIcon={() => <DesktopIcon />}
                      otherOptions={{label: 'Other', value: 'other', position: 'bottom', enabled: true}}
                      emptyOption={{enabled: false}}
                      placeholder="Select a platform…"/>
                  </Box>

                  {!isManualPlatform && selectedPlatform && !hidePlatformId && (
                    <Box mt="2">
                      {!selectedPlatform.has_global_taxonomy && !selectedCountry ? (
                        <Text>Please select a country</Text>
                      ) : audienceItem?.platform_code ? (
                        <Button
                          size="2"
                          variant="soft"
                          color="iris"
                          onClick={handleCopyId}
                          style={{
                            backgroundColor: 'var(--iris-3)',
                            color: 'var(--iris-11)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            padding: '10px 14px',
                          }}
                        >
                          {copyStatus === 'copied' ? (
                            'Copied!'
                          ) : copyStatus === 'error' ? (
                            'Unable to copy'
                          ) : (
                            <>
                              <CopyIcon aria-hidden="true" />
                              ID: {audienceItem?.platform_code}
                            </>
                          )}
                        </Button>
                      ) : (
                        <Text>ID unavailable</Text>
                      )}
                    </Box>
                  )}
                </Box>

                <Box width="50%">
                  <Heading color="gray" as="h3" size="2">Reach</Heading>
                  <hr />
                  <Tooltip content={categoryDisplayName === "Business" ? "Number of Businesses" : "Number of Households"}><HomeIcon /></Tooltip>
                  <Text ml="2">{selectedCountry ? (audienceItem?.reach_stats?.num_households === 0 || audienceItem?.reach_stats?.num_households == null ? '–' : audienceItem?.reach_stats?.num_households.toLocaleString()) : '–'}</Text>
                  <br />
                  {
                    categoryDisplayName === "Business" ? (
                      <Tooltip content="Number of IPs"><GlobeIcon /></Tooltip>
                    ) : (
                      <Tooltip content="Number of Persons"><PersonIcon /></Tooltip>
                    )
                  }
                  <Text ml="2">{selectedCountry ? (audienceItem?.reach_stats?.num_persons === 0 || audienceItem?.reach_stats?.num_persons == null ? '–' : audienceItem?.reach_stats?.num_persons.toLocaleString()) : '–'}</Text>
                  <br />
                  <Tooltip content="Number of Devices"><DesktopIcon /></Tooltip>
                  <Text ml="2">{selectedCountry ? (audienceItem?.reach_stats?.num_devices === 0 || audienceItem?.reach_stats?.num_devices == null ? '–' : audienceItem?.reach_stats?.num_devices.toLocaleString()) : '–'}</Text>
                  {!selectedCountry && <Text><em>Please select country to see reach</em></Text>}
                </Box>
              </Flex>

              <Box mt="auto">
                {isCopyIdPlatform ? (
                  <Button
                    className="fullwidth"
                    size="3"
                    disabled={!canCopyId}
                    onClick={handleCopyId}
                    title={!canCopyId ? 'Select a country to get this audience ID' : undefined}
                  >
                    {copyStatus === 'copied' ? (
                      'Copied!'
                    ) : copyStatus === 'error' ? (
                      'Unable to copy'
                    ) : (
                      <>
                        <CopyIcon aria-hidden="true" />
                        Copy ID
                      </>
                    )}
                  </Button>
                ) : hidePlatformId ? (
                  <Callout.Root variant="soft" color="gray" style={{ justifyContent: 'center' }}>
                    <Callout.Text style={{ textAlign: 'center' }}>
                      Search for &ldquo;{audienceItem?.display_name}&rdquo; in {selectedPlatform?.display_name}.
                    </Callout.Text>
                  </Callout.Root>
                ) : (
                  <Button className="fullwidth" size="3" onClick={() => setStep(2)}>
                    Request Audience <ArrowRightIcon />
                  </Button>
                )}
              </Box>
            </>
          )}

          {step === 2 && (
            <AudienceRequestForm
              onBack={() => setStep(1)}
              onClose={onOpenChange ? () => onOpenChange(false) : undefined}
              onSubmit={handleFormSubmit}
              onPlatformSelected={onPlatformSelected}
              platforms={platforms}
              audienceName={audienceItem?.display_name || ''}
              audienceCategory={categoryDisplayName || ''}
              countrySelected={selectedCountry?.display_name || ''}
              selectedPlatform={selectedPlatform}
              requestType="classic"
            />
          )}
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AudienceRecommendationModal;
